import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentPage,
  InputTextarea,
  ModalForm,
  NoResults,
  Row,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import ValueInputModal from "../ValueInputModal";
import { DomainStatus, escapeHtml, formatBRL, formatCompetencia, formatDate } from "../ow";

const VALUE_STATUS = { PENDING: "pendente", APPROVED: "aprovado", REJECTED: "reprovado" };

const REASON_MAX_LENGTH = 500;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
`;

// Botão fora do flex da linha: o $noWidth da lib vira width: inherit e esticaria
const Fixed = styled.div`
  flex-shrink: 0;
`;

const Reason = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const competenceOf = (input) => ({ month: Number(input.reference_month), year: Number(input.reference_year) });

/** Nome (ou texto) em cima e linhas menores embaixo, como as colunas de duas linhas da R.7d. */
function Stacked({ main, lines = [], mainVariant = "paragraph_small_medium" }) {
  return (
    <Lines>
      <Text $text={escapeHtml(main || "—")} $variant={mainVariant} $color="N900" />
      {lines
        .filter(Boolean)
        .map((line, index) => (
          <Text key={index} $text={escapeHtml(line.text)} $variant="caption_medium" $color={line.danger ? "E400" : "N400"} />
        ))}
    </Lines>
  );
}

/**
 * Aba de Valores Variáveis — histórico de inputs + ações de aprovação.
 * Visão da empresa: ADM ou quem tem "Ver cronogramas" (só leitura).
 *
 * Props:
 * - contractId: number
 * - contract: object
 * - canManage: boolean (informar, aprovar e rejeitar; padrão true, que era o uso só do ADM)
 */
export default function VariableValuesTab({ contractId, contract, canManage = true }) {
  const valueInputService = useService("contract-value-inputs");
  const currentUser = JSON.parse(localStorage.getItem("user_ow") || "null");

  const [inputs, setInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  // Value Input Modal
  const [showValueInput, setShowValueInput] = useState(false);

  // Reprovar
  const [selectedInput, setSelectedInput] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const busy = approvingId !== null || rejecting;

  const fetchInputs = useCallback(async () => {
    setLoading(true);
    try {
      // listPending com todos status e filtro por contractId
      const result = await valueInputService.listPending(null, contractId);
      if (result?.data) {
        setInputs(Array.isArray(result.data) ? result.data : result.data.list || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar valores");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  useEffect(() => {
    if (contractId) fetchInputs();
  }, [contractId, fetchInputs]);

  async function handleApprove(inputId) {
    setApprovingId(inputId);
    try {
      const result = await valueInputService.approve(inputId);
      if (result && !result.error) {
        toast.success("Valor aprovado com sucesso!");
        fetchInputs();
      } else {
        toast.error(result?.msg || "Erro ao aprovar valor");
      }
    } catch (err) {
      toast.error("Erro ao aprovar valor");
    }
    setApprovingId(null);
  }

  function openReject(input) {
    setSelectedInput(input);
    setReason("");
    setReasonError("");
  }

  function closeReject() {
    if (rejecting) return;
    setSelectedInput(null);
  }

  async function handleRejectConfirm() {
    // O back só exige motivo não vazio: a tela não recusa o que ele aceita
    if (!reason.trim()) {
      setReasonError("Informe o motivo.");
      return;
    }
    setRejecting(true);
    try {
      const result = await valueInputService.reject(selectedInput.id, reason.trim());
      if (result && !result.error) {
        toast.success("Valor rejeitado");
        setSelectedInput(null);
        fetchInputs();
      } else {
        toast.error(result?.msg || "Erro ao rejeitar valor");
      }
    } catch (err) {
      toast.error("Erro ao rejeitar valor");
    }
    setRejecting(false);
  }

  const columns = [
    {
      id: "competence",
      header: "Competência",
      size: 110,
      cell: ({ row }) => <Text $text={formatCompetencia(competenceOf(row.original))} $variant="paragraph_small_bold" $color="N900" />,
    },
    {
      id: "value",
      header: "Valor",
      size: 130,
      align: "right",
      cell: ({ row }) => <Text $text={formatBRL(row.original.value)} $variant="paragraph_small_bold" $color="N900" />,
    },
    {
      id: "status",
      header: "Status",
      size: 110,
      cell: ({ row }) => <DomainStatus domain="valorVariavel" value={VALUE_STATUS[row.original.status] || row.original.status} />,
    },
    {
      id: "inputtedBy",
      header: "Informado por",
      size: 220,
      maxSize: 280,
      cell: ({ row }) => (
        <Stacked
          main={row.original.inputtedBy?.name}
          lines={[
            row.original.inputtedBy?.email ? { text: row.original.inputtedBy.email } : null,
            row.original.inputted_at ? { text: formatDate(row.original.inputted_at) } : null,
          ]}
        />
      ),
    },
    {
      id: "approvedBy",
      header: "Aprovado por",
      size: 160,
      cell: ({ row }) => (
        <Stacked
          main={row.original.approvedBy?.name}
          lines={[row.original.approved_at ? { text: formatDate(row.original.approved_at) } : null]}
        />
      ),
    },
    {
      id: "notes",
      header: "Observações",
      size: 200,
      maxSize: 320,
      cell: ({ row }) => (
        <Stacked
          main={row.original.notes}
          lines={[
            row.original.status === "REJECTED" && row.original.rejection_reason
              ? { text: `Motivo: ${row.original.rejection_reason}`, danger: true }
              : null,
          ]}
        />
      ),
    },
  ];

  if (canManage) {
    columns.push({
      id: "actions",
      size: 220,
      maxSize: 220,
      align: "right",
      cell: ({ row }) => {
        const input = row.original;
        if (input.status !== "PENDING") return null;
        const ownInput = input.inputtedBy?.id === currentUser?.id;
        const approve = (
          <Button
            $text="Aprovar"
            $variant="outline"
            $size="small"
            $noWidth
            $isLoading={approvingId === input.id}
            $isDisabled={busy || ownInput}
            $action={() => handleApprove(input.id)}
          />
        );
        return (
          <Actions>
            {ownInput ? (
              <Tooltip content="Você não pode aprovar um valor que você mesmo submeteu" $position="top" $size="medium" $color="dark">
                {approve}
              </Tooltip>
            ) : (
              approve
            )}
            <Button
              $text="Reprovar"
              $variant="outline_danger"
              $size="small"
              $noWidth
              $isDisabled={busy}
              $action={() => openReject(input)}
            />
          </Actions>
        );
      },
    });
  }

  return (
    <ContentPage>
      <Row $justifyContent="space-between" $alignItems="center" $gap={16}>
        <Text $text="Histórico de valores informados para este contrato." $variant="paragraph_small_medium" $color="N700" />
        {canManage && contract?.status === "ACTIVE" ? (
          <Fixed>
            <Button $text="Informar valor" $variant="primary" $noWidth $action={() => setShowValueInput(true)} />
          </Fixed>
        ) : null}
      </Row>

      <BasicTable
        data={inputs}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={loading}
        noResults={<NoResults notResultText="Nenhum valor informado para este contrato" />}
      />

      <ValueInputModal
        show={showValueInput}
        onHide={() => setShowValueInput(false)}
        contract={contract}
        valueInputService={valueInputService}
        onSuccess={() => {
          toast.success(contract.requires_value_approval ? "Valor submetido e aguardando aprovação!" : "Valor aprovado automaticamente!");
          fetchInputs();
        }}
      />

      {selectedInput && (
        <ModalForm
          $showModal={!!selectedInput}
          $setShowModal={closeReject}
          $noClose={rejecting}
          $isLoading={rejecting}
          $size="medium"
          $title={`Reprovar valor de ${formatCompetencia(competenceOf(selectedInput), true)}`}
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeReject, variant: "outline", isDisabled: rejecting },
            { text: "Reprovar valor", action: handleRejectConfirm, variant: "danger", isDisabled: rejecting },
          ]}
        >
          <Reason>
            <Text
              $text="%s · %s · <strong>%s</strong>"
              $replaces={[
                escapeHtml(contract?.contractor?.name || "Prestador"),
                formatCompetencia(competenceOf(selectedInput)),
                formatBRL(selectedInput.value),
              ]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            <InputTextarea
              $idForm="value-rejection-reason"
              $label="Motivo da reprovação"
              $variant="default"
              $isRequired
              $errors={reasonError}
              $description="Quem informou o valor vê este motivo no histórico."
              maxLength={REASON_MAX_LENGTH}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError("");
              }}
            />
          </Reason>
        </ModalForm>
      )}
    </ContentPage>
  );
}
